/**
 * 头像种子 → 该怎么画。纯函数，`Seat.tsx` 与设置面板的预览各调一次，不重复约定。
 *
 * `avatarSeed` 的四种取值：
 *   - `''`（默认）              → 没有自定义头像，退回昵称首字 + 身份色
 *   - `'emoji:😀'`              → 一个 emoji，直接嵌在字符串里，不用另开字段
 *   - `'custom'`                → 用户上传过图片；真正的地址不存在 avatarSeed 里
 *     （存不下也没必要——图片挂在 `/api/avatars/:accountId`，accountId 本来就有）
 *   - `'bot:tag'` / `'bot:tag#3'` → 机器人内置头像，静态素材不走账号上传接口。
 *     格式是 `bot:<角色>#<配饰号>`：`#` 前是**角色**（素材文件夹名），不是这个机器人的性格——
 *     服务端先随机角色、再随机配饰，大模型 AI 完全可以是 `bot:tag#3`
 *     （`server/room/botNames.ts` 的 `pickBotAvatarSeed`）。不带 `#` 的是旧 seed，按 0 号配饰画。
 *     机器人的真实性格另有 `botPersona` 字段，画头像时**不要**去读它。
 */
import { BOT_AVATAR_VARIANTS, type BotPersona } from '@poker/shared'

export type SeatAvatar =
  | { readonly kind: 'default' }
  | { readonly kind: 'emoji'; readonly char: string }
  | { readonly kind: 'custom'; readonly url: string }
  /** 同一角色固定 7 张完整原画；variant 只用于稳定选图，不再驱动前端叠层。 */
  | { readonly kind: 'bot'; readonly url: string; readonly variant: number }

const EMOJI_PREFIX = 'emoji:'
const CUSTOM_MARK = 'custom'
const BOT_PREFIX = 'bot:'

/**
 * 七个角色（黑猫、狐狸、乌龟、熊、老虎、猫头鹰、仿生人）各有七张独立完成的原画：
 * 礼帽、贝雷帽、月桂冠、口罩、丝巾、胸针与肩章。
 * 配饰已在插画中与毛发、耳朵、面部和衣料一起重绘；这里仅负责稳定选图，不能再用
 * SVG/CSS 在头像上拼装配饰。
 *
 * ★ 角色文件夹沿用了性格的名字（`tag` / `agent` …），但**角色与性格已经脱钩**：
 *   任何机器人都可能画成任何角色，同桌只保证不撞脸（产品决定：脸负责分清谁是谁，
 *   打法由名字暗示）。所以这里只认 seed 里的角色，不要改成按 `botPersona` 取图，
 *   也不要把「角色 ≠ 性格」的 seed 当成坏数据退回默认头像。
 * ★ 文件夹名不能改：旧客户端缓存着这些路径。
 */
export const BOT_AVATAR_VARIANT_SLUGS = Object.freeze([
  'top-hat',
  'beret',
  'laurel',
  'mask',
  'scarf',
  'brooch',
  'epaulette',
] as const)

function botAvatarUrls(persona: BotPersona): readonly string[] {
  return Object.freeze(BOT_AVATAR_VARIANT_SLUGS.map((slug) => `/avatars/bots/${persona}/${slug}.webp`))
}

const BOT_AVATAR_URLS: Readonly<Record<BotPersona, readonly string[]>> = Object.freeze({
  tag: botAvatarUrls('tag'),
  lag: botAvatarUrls('lag'),
  rock: botAvatarUrls('rock'),
  station: botAvatarUrls('station'),
  maniac: botAvatarUrls('maniac'),
  balanced: botAvatarUrls('balanced'),
  agent: botAvatarUrls('agent'),
})

function isBotPersona(value: string): value is BotPersona {
  return Object.prototype.hasOwnProperty.call(BOT_AVATAR_URLS, value)
}

/**
 * 「自己这张自定义头像换过几次」的计数。
 *
 * ★ 为什么需要它：`/api/avatars/:accountId` 这个地址**换头像也不会变**（服务端按
 *   accountId 原地覆盖，不建资产表）。服务端那边已经用 ETag 保证了「再请求一次会拿到新图」，
 *   但同一个页面里那个 `<img>` 的 src 一个字都没变 —— React 不会去改一个没变的属性，
 *   浏览器自然也不会重新发请求，于是**第二次上传之后屏幕上还是第一张图**，
 *   正是 ETag 那次修复想根治、但只治了一半的症状。
 *   给自己的头像地址挂一个每次上传就 +1 的 `?v=`，元素属性真的变了，图才会换。
 * ★ 只用于「我自己的」头像（大厅入口 + 选择面板）：别人的头像不由本机上传触发，
 *   跟着一起 bust 只会让同桌白白重下一遍。
 */
let ownAvatarVersionCounter = 0

export function bumpOwnAvatarVersion(): number {
  ownAvatarVersionCounter += 1
  return ownAvatarVersionCounter
}

export function ownAvatarVersion(): number {
  return ownAvatarVersionCounter
}

export function resolveAvatar(avatarSeed: string, accountId: string, version = 0): SeatAvatar {
  if (avatarSeed === CUSTOM_MARK && accountId !== '') {
    const base = `/api/avatars/${accountId}`
    return { kind: 'custom', url: version > 0 ? `${base}?v=${String(version)}` : base }
  }
  if (avatarSeed.startsWith(EMOJI_PREFIX)) {
    const char = avatarSeed.slice(EMOJI_PREFIX.length)
    if (char !== '') return { kind: 'emoji', char }
  }
  if (avatarSeed.startsWith(BOT_PREFIX)) {
    // `bot:tag#3` → 角色 'tag'、配饰 3（不管这个机器人是什么性格）；没有 `#` 就是 0（存量种子原样可用）。
    // 变量名沿用 persona 只因角色文件夹借用了性格的名字，见上面 BOT_AVATAR_VARIANT_SLUGS 的注释。
    const body = avatarSeed.slice(BOT_PREFIX.length)
    const hash = body.indexOf('#')
    const persona = hash < 0 ? body : body.slice(0, hash)
    const variant = hash < 0 ? 0 : Number.parseInt(body.slice(hash + 1), 10)
    if (isBotPersona(persona)) {
      const pool = BOT_AVATAR_URLS[persona]
      const n = Number.isSafeInteger(variant) && variant >= 0 ? variant : 0
      return {
        kind: 'bot',
        url: pool[n % pool.length] ?? pool[0] ?? '',
        variant: n % BOT_AVATAR_VARIANTS,
      }
    }
  }
  return { kind: 'default' }
}

/** 反方向：把选中的 emoji 编码成要发给 `PATCH /me` 的 avatarSeed。 */
export function emojiAvatarSeed(char: string): string {
  return `${EMOJI_PREFIX}${char}`
}

/** 服务端上传成功后固定回填这个标记；单独导出是为了两端不各自拼一遍字面量。 */
export const CUSTOM_AVATAR_SEED = CUSTOM_MARK
